/**
 * Minimal formatting for Smart Engage API response.
 * Normalizes display values only (e.g. status for styling). No restructuring.
 */

export const formatSmartEngageData = (response) => {
  if (!response) return null;

  const campaign_summary = Array.isArray(response.campaign_summary)
    ? response.campaign_summary.map((c) => ({
        ...c,
        status: typeof c.status === 'string' ? c.status.toLowerCase() : c.status,
      }))
    : [];

  return {
    scorecards: response.scorecards ?? [],
    campaign_summary,
  };
};

/**
 * Normalizes `/smart_engage/campaigns` payload to an array of `{ campaign, records }`.
 * Keeps rendering/sagas safe if the API returns a non-array on error or schema change.
 */
export const normalizeCampaignFramesResponse = (response) =>
  Array.isArray(response) ? response : [];

/** Maps the API's camelCase campaign key onto the snake_case key the organisms use. */
const CAMPAIGN_KEY_REMAP = { reEngagementWinback: 're_engagement' };

/**
 * Normalizes `/promomechanics` payload.
 * Shape: { views: { <campaign_key>: { tiers: Array<tier> } } }
 * Remaps the campaign key and reads the API's camelCase tier fields (snake fallback for safety).
 * Only campaigns with a non-empty tiers array are kept, so the UI stays fully data-driven.
 */
export const normalizePromoMechanicsResponse = (response) => {
  if (!response || typeof response.views !== 'object' || response.views === null) return null;

  const views = {};
  Object.keys(response.views).forEach((rawKey) => {
    const rawTiers = response.views[rawKey]?.tiers;
    if (!Array.isArray(rawTiers) || rawTiers.length === 0) return;

    const key = CAMPAIGN_KEY_REMAP[rawKey] ?? rawKey;
    views[key] = {
      tiers: rawTiers.map((t) => ({
        discountLabel: t?.discountLabel ?? t?.discount_label ?? '',
        betterPerformer: !!(t?.betterPerformer ?? t?.better_performer),
        daysLeft: t?.daysLeft ?? t?.days_left ?? null,
        returnRate: t?.returnRatePct ?? t?.return_rate ?? null,
        redemptionRate: t?.redemptionRatePct ?? t?.redemption_rate ?? null,
        redeemedOrders: t?.redeemedOrderCount ?? t?.redeemed_orders ?? null,
        totalDiscount: t?.totalDiscount ?? t?.total_discount ?? null,
      })),
    };
  });

  return { views };
};

/**
 * Normalizes `/outlet_perfromance` payload.
 * The API returns a flat `outlets[]` array (each with per-campaign metrics); the
 * OutletRanking organism expects a nested `views.<key>.clusters[].outlets[]` shape.
 * Remaps campaign keys, metric field names, and geo keys, and wraps all outlets in a
 * single synthetic cluster per view (cluster name is never displayed).
 */
export const normalizeOutletRankingResponse = (response) => {
  if (!response || !Array.isArray(response.outlets)) return null;

  const rawCampaignKeys =
    Array.isArray(response.campaigns) && response.campaigns.length
      ? response.campaigns.map((c) => c.key)
      : Object.keys(response.outlets[0]?.campaigns ?? {});

  const views = {};
  rawCampaignKeys.forEach((rawKey) => {
    if (!rawKey) return;
    const viewKey = CAMPAIGN_KEY_REMAP[rawKey] ?? rawKey;

    const outlets = response.outlets.map((o) => {
      const c = (o.campaigns && o.campaigns[rawKey]) || {};
      const lat = o.geo?.latitude ?? null;
      const lng = o.geo?.longitude ?? null;
      return {
        outlet: o.outletName ?? o.shortName ?? o.outletId ?? '',
        returns: c.returnCustomerCount ?? null,
        redeemed: c.redeemedOrderCount ?? null,
        discount: c.totalDiscount ?? null,
        geo: lat !== null && lng !== null ? { lat, lng } : null,
      };
    });

    views[viewKey] = { clusters: [{ name: 'all', outlets }] };
  });

  return { views };
};

export const normalizeTimingPatternsResponse = (response) => {
  if (!response) return null;

  const day_parts = Array.isArray(response.dayParts)
    ? response.dayParts.map(dp => ({
        key: dp.key ?? null,
        label: dp.label ?? dp.key ?? null,
        time: dp.timeRange ?? dp.time ?? null,
      }))
    : Array.isArray(response.day_parts) ? response.day_parts : [];

  const days = Array.isArray(response.days)
    ? response.days.map(d =>
        typeof d === 'string' ? { key: d, label: d } : { key: d.key ?? null, label: d.label ?? d.key ?? null }
      )
    : [];

  const metrics = Array.isArray(response.metrics) ? response.metrics : [];

  const rawViews = response.views && typeof response.views === 'object' ? response.views : {};
  const views = {};
  Object.entries(rawViews).forEach(([rawKey, view]) => {
    const key = CAMPAIGN_KEY_REMAP[rawKey] ?? rawKey;
    if (!view || typeof view !== 'object') { views[key] = {}; return; }

    const cells = Array.isArray(view.cells)
      ? view.cells.map(c => ({
          day_part: c.dayPart ?? c.day_part ?? null,
          day: c.dayLabel ?? c.day ?? null,
          counts: c.counts && typeof c.counts === 'object' ? c.counts : {},
          sharePct: c.sharePct && typeof c.sharePct === 'object' ? c.sharePct : {},
        }))
      : [];

    const row_totals = Array.isArray(view.dayPartTotals)
      ? view.dayPartTotals.map(r => ({
          day_part: r.dayPart ?? null,
          counts: r.counts && typeof r.counts === 'object' ? r.counts : {},
          sharePct: r.sharePct && typeof r.sharePct === 'object' ? r.sharePct : {},
        }))
      : Array.isArray(view.row_totals) ? view.row_totals : [];

    const column_totals = Array.isArray(view.dayTotals)
      ? view.dayTotals.map(c => ({
          day: c.dayLabel ?? null,
          counts: c.counts && typeof c.counts === 'object' ? c.counts : {},
          sharePct: c.sharePct && typeof c.sharePct === 'object' ? c.sharePct : {},
        }))
      : Array.isArray(view.column_totals) ? view.column_totals : [];

    views[key] = {
      cells,
      row_totals,
      column_totals,
      totals: view.totals && typeof view.totals === 'object' ? view.totals : {},
      heatScale: view.heatScale && typeof view.heatScale === 'object' ? view.heatScale : {},
    };
  });

  return { day_parts, days, metrics, views, insight: response.insight ?? null };
};
